import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';
import { camelCase } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { getMatch, updateMatch, getShooters } from '../../api';
import { getAllDivisions, getAllCategories, getFlagSrc } from '../../utils/scoring';
import SaveButton from '../../components/SaveButton';
import Snackbar from '../../components/Snackbar';

export default function Teams() {
  const { matchId } = useParams();
  const { match: ctxMatch } = useOutletContext();
  const { t } = useTranslation();
  const [teams, setTeams] = useState([]);
  const [shooters, setShooters] = useState([]);
  const [matchType, setMatchType] = useState(ctxMatch?.type || 'handgun');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [matchRes, shootersRes] = await Promise.all([
        getMatch(matchId),
        getShooters(matchId).catch(() => ({ data: [] })),
      ]);
      setTeams(matchRes.data.teams || []);
      setMatchType(matchRes.data.type || 'handgun');
      setShooters(Array.isArray(shootersRes.data) ? shootersRes.data : []);
    } catch {
      setSnack({ open: true, message: t('common.saveError'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [matchId, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const changeTeam = (id, updated) => {
    setTeams((prev) => prev.map((tm) => (tm.id === id ? updated : tm)));
  };

  const deleteTeam = (id) => {
    setTeams((prev) => prev.filter((tm) => tm.id !== id));
  };

  const addTeam = () => {
    setTeams((prev) => [...prev, { id: uuid(), title: '', type: 'generic' }]);
  };

  const handleSave = async () => {
    if (teams.some((tm) => !tm.title)) {
      setShowErrors(true);
      setSnack({ open: true, message: t('common.saveError'), severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      await updateMatch(matchId, { teams });
      setSnack({ open: true, message: t('common.saveSuccess'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || t('common.saveError'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const divisions = getAllDivisions({ matchType });
  const categories = getAllCategories({ matchType });

  return (
    <Box sx={{ maxWidth: 720, width: '90%', mx: 'auto', pb: 3 }}>
      <Grid container spacing={2}>
        {teams.map((team) => (
          <Grid key={team.id} size={{ xs: 12, md: 6 }}>
            <TeamCard
              team={team}
              matchType={matchType}
              divisions={divisions}
              categories={categories}
              shooters={shooters}
              showErrors={showErrors}
              onChange={(updated) => changeTeam(team.id, updated)}
              onDelete={() => deleteTeam(team.id)}
              t={t}
            />
          </Grid>
        ))}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{
            height: '100%', minHeight: 120, display: 'flex', alignItems: 'center',
            justifyContent: 'center', background: 'rgba(0,0,0,0.03)',
          }}>
            <Fab color="primary" onClick={addTeam}>
              <AddIcon />
            </Fab>
          </Paper>
        </Grid>
      </Grid>

      {teams.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <SaveButton onClick={handleSave} saving={saving} label={t('common.save')} />
        </Box>
      )}

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity}
        onClose={() => setSnack({ ...snack, open: false })} />
    </Box>
  );
}

function TeamCard({ team, matchType, divisions, categories, shooters, showErrors, onChange, onDelete, t }) {
  const members = shooters.filter((s) => s.team === team.id);
  const hasMembers = members.length > 0;
  const exceeds4 = members.length > 4;

  const shouldShowShooterError = (shooter) => {
    if (team.type === 'generic' || team.type === 'family') return false;
    const parts = team.type.split('-');
    const cat = parts[0];
    const div = parts.slice(1).join('-');
    if (shooter.division !== div) return true;
    if (cat !== 'regular' && (shooter.category || 'regular') !== cat) return true;
    return false;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          sx={{ flex: 1 }}
          label={t('teams.teamName')}
          value={team.title}
          error={showErrors && !team.title}
          onChange={(e) => onChange({ ...team, title: e.target.value })}
          fullWidth
          variant="standard"
        />
        <IconButton
          disabled={hasMembers}
          color="error"
          onClick={onDelete}
        >
          <DeleteForeverIcon fontSize="small" />
        </IconButton>
      </Box>

      <Select
        sx={{ mt: 1.5 }}
        value={team.type}
        onChange={(e) => onChange({ ...team, type: e.target.value })}
        fullWidth
        variant="standard"
      >
        <MenuItem value="generic">{t('teams.typeGeneric')}</MenuItem>
        {categories.map((cat) => divisions.map((div) => (
          <MenuItem key={`${cat}-${div}`} value={`${cat}-${div}`}>
            {t(`divisions.${camelCase(div)}`, div)}
            {cat !== 'regular' && (
              <span>&nbsp;-&nbsp;{t(`categories.${camelCase(cat)}`, cat)}</span>
            )}
          </MenuItem>
        )))}
        <MenuItem value="family">{t('teams.typeFamily')}</MenuItem>
      </Select>

      {exceeds4 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 0.5 }}>
          <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />
          <Typography variant="caption">{t('teams.exceeds4')}</Typography>
        </Box>
      )}

      <Paper sx={{ p: 2, mt: 1.5, flex: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t('teams.numShooters', { count: members.length })}
        </Typography>
        {members.map((shooter) => {
          const mismatch = shouldShowShooterError(shooter);
          const parts = (shooter.publicId || '').split('|');
          const displayId = parts[parts.length - 1];
          const countryCode = parts.length > 1 ? parts[0] : null;

          return (
            <Box key={shooter.id} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              {mismatch && (
                <Tooltip title={t('teams.shooterMismatch')}>
                  <WarningIcon fontSize="small" sx={{ color: 'error.main', mr: 0.5 }} />
                </Tooltip>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {countryCode && (
                  <img alt={countryCode} src={getFlagSrc({ countryCode, size: 16 })} style={{ display: 'block' }} />
                )}
                <Typography variant="subtitle2">{displayId}</Typography>
                <Typography variant="subtitle2">/</Typography>
                <Typography variant="subtitle2">{shooter.name}</Typography>
              </Box>
              {shooter.club && (
                <Typography variant="body2" sx={{ ml: 0.5, flex: 1, opacity: 0.6 }}>
                  ({shooter.club})
                </Typography>
              )}
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
}
